import React, { type ErrorInfo } from 'react';
import ReactDOM from 'react-dom/client';
import { type Dispatch } from 'redux';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { jwtDecode } from 'jwt-decode';
import 'moment-timezone';
import 'isomorphic-fetch';

import { App } from './app/App';
import { ConfigProviderLayout } from './features/layout';
import reportWebVitals from './reportWebVitals';

import { rootStore } from './store/rootStore';
import { defaultTheme } from './features/theme';
import ThemeProvider from './features/theme/ThemeProvider';
import { Fallback } from './features/pages/Fallback/Fallback';

import './assets/variabless.scss';
import { sessionStorage } from './utils/storage';
import { isMobilePhone } from './utils/device';
import api from 'src/app/apiSingleton';
import { setUserProfile } from './store/sessions';
import type { IProfile } from './types/user/types';

import * as Sentry from '@sentry/react';

const environment = process.env.NODE_ENV;

Sentry.init({
  dsn: 'https://1ef5ce1c76d3addf91a8834b42783147@o4511070054580224.ingest.de.sentry.io/4511070061133904',
  integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration()],
  sendDefaultPii: true,
  environment: environment,
  enableLogs: true,
  tracesSampleRate: environment === 'production' ? 0.5 : 1.0,
  profileSessionSampleRate: environment === 'production' ? 0.5 : 1.0,
  initialScope: {
    tags: { is_mobile: isMobilePhone() }
  },
  // Android in-app browsers (e.g. Telegram) inject scripts that call their native Java bridge;
  // when the bridge lacks the method, Chromium throws these. We never use a WebView bridge.
  ignoreErrors: [/^Error invoking \w+: (Method not found|Java object is gone|Unknown Java object ID)$/]
});

// Sentry.ErrorBoundary has already captured the exception as an Issue (eventId) before calling this,
// so we only add a structured log with the full details instead of capturing it a second time.
function logError(error: unknown, componentStack: string, eventId: string) {
  const isError = error instanceof Error;
  const message = isError ? error.message : String(error);

  Sentry.logger.error(Sentry.logger.fmt`ErrorBoundary caught: ${message}`, {
    eventId,
    errorName: isError ? error.name : typeof error,
    stack: isError ? error.stack : undefined,
    componentStack,
    url: window.location.href
  });

  console.error('Caught an error:', error, componentStack, eventId);
}

function initUserProfile(dispatch: Dispatch) {
  const token = sessionStorage.get('sessionToken');

  if (token) {
    const user: IProfile = jwtDecode(token);

    api.apiClient.setToken(token);
    dispatch(setUserProfile(user));

    Sentry.setUser({
      username: user.pib
    });
  }
}

rootStore.dispatch(initUserProfile);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <Sentry.ErrorBoundary fallback={Fallback} onError={logError}>
    <Provider store={rootStore}>
      <ConfigProvider theme={defaultTheme}>
        <ThemeProvider>
          <ConfigProviderLayout>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ConfigProviderLayout>
        </ThemeProvider>
      </ConfigProvider>
    </Provider>
  </Sentry.ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
